// Phase 8 R1 — Canned Job selector dialog.
// Phase 8 R4 — Favorites + Recent + All sections, single-click Quick Apply,
// debounced search, keyboard navigation, memoized rows. No pricing/learning side effects.

import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCannedJobs, type CannedJobListItem } from '@/hooks/cannedJobs/useCannedJobs';
import { useApplyCannedJob } from '@/hooks/cannedJobs/useApplyCannedJob';
import { useRecentCannedJobs } from '@/hooks/cannedJobs/useRecentCannedJobs';
import { useUserRole } from '@/hooks/useUserRole';
import { Link } from 'react-router-dom';
import { Loader2, PackageCheck, Star, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FavoriteToggle } from './FavoriteToggle';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  shopId: string;
  ticketId: string;
}

const RECENT_VISIBLE = 5;

export function CannedJobSelectorDialog({ open, onOpenChange, shopId, ticketId }: Props) {
  // Lazy-mount: render nothing until dialog opens (perf).
  if (!open) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <SelectorBody shopId={shopId} ticketId={ticketId} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function SelectorBody({
  shopId,
  ticketId,
  onClose,
}: {
  shopId: string;
  ticketId: string;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [category, setCategory] = useState<string>('__all__');
  const [focusIndex, setFocusIndex] = useState(0);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data: allJobs = [], isLoading } = useCannedJobs(shopId, {});
  const { recentIds } = useRecentCannedJobs(shopId);
  const applyMut = useApplyCannedJob();

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const j of allJobs) if (j.category) set.add(j.category);
    return Array.from(set).sort();
  }, [allJobs]);

  // Apply a canned job immediately (Quick Apply).
  const handleApply = useCallback(
    async (job: CannedJobListItem) => {
      if (applyMut.isPending) return;
      setPendingId(job.id);
      try {
        await applyMut.mutateAsync({ shopId, ticketId, cannedJobId: job.id });
        onClose();
      } catch {
        // toast handled in hook
      } finally {
        setPendingId(null);
      }
    },
    [applyMut, shopId, ticketId, onClose],
  );

  // Filter by category + search (used as base for sectioning).
  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    return allJobs.filter((j) => {
      if (category !== '__all__' && (j.category ?? '') !== category) return false;
      if (!q) return true;
      return (
        j.name.toLowerCase().includes(q) ||
        (j.description ?? '').toLowerCase().includes(q) ||
        (j.category ?? '').toLowerCase().includes(q)
      );
    });
  }, [allJobs, category, deferredSearch]);

  const isSearching = deferredSearch.trim().length > 0;

  // Section composition. When searching, collapse to one flat list.
  const { favorites, recent, all, flat } = useMemo(() => {
    if (isSearching) {
      return { favorites: [], recent: [], all: [], flat: filtered };
    }
    const favs = filtered
      .filter((j) => j.is_favorite)
      .sort(
        (a, b) =>
          a.favorite_order - b.favorite_order || a.name.localeCompare(b.name),
      );
    const favIds = new Set(favs.map((j) => j.id));

    const recents: CannedJobListItem[] = [];
    for (const id of recentIds) {
      if (favIds.has(id)) continue;
      const job = filtered.find((j) => j.id === id);
      if (job) recents.push(job);
      if (recents.length >= RECENT_VISIBLE) break;
    }
    const recentIdsSet = new Set(recents.map((j) => j.id));

    const rest = filtered
      .filter((j) => !favIds.has(j.id) && !recentIdsSet.has(j.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { favorites: favs, recent: recents, all: rest, flat: [] };
  }, [filtered, isSearching, recentIds]);

  // Linear focus order for keyboard navigation.
  const orderedJobs = useMemo<CannedJobListItem[]>(
    () => (isSearching ? flat : [...favorites, ...recent, ...all]),
    [isSearching, flat, favorites, recent, all],
  );

  // Reset focus on data shape change.
  useEffect(() => {
    setFocusIndex(0);
  }, [orderedJobs.length, isSearching]);

  // Keyboard navigation (skip when typing in input).
  const containerRef = useRef<HTMLDivElement>(null);
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const inInput =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
        target.getAttribute('role') === 'combobox';

      if (e.key === 'ArrowDown') {
        if (orderedJobs.length === 0) return;
        e.preventDefault();
        setFocusIndex((i) => Math.min(orderedJobs.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        if (orderedJobs.length === 0) return;
        e.preventDefault();
        setFocusIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter' && !inInput) {
        const job = orderedJobs[focusIndex];
        if (job) {
          e.preventDefault();
          handleApply(job);
        }
      }
    },
    [orderedJobs, focusIndex, handleApply],
  );

  const renderRow = (job: CannedJobListItem, globalIdx: number) => (
    <CannedJobRow
      key={job.id}
      job={job}
      shopId={shopId}
      focused={globalIdx === focusIndex}
      pending={pendingId === job.id}
      anyPending={applyMut.isPending}
      onApply={handleApply}
      onFocus={() => setFocusIndex(globalIdx)}
    />
  );

  return (
    <div onKeyDown={onKeyDown} ref={containerRef}>
      <DialogHeader>
        <DialogTitle>Add Canned Job</DialogTitle>
        <DialogDescription>
          Click a template to apply it to this ticket. Parts and labor can be
          edited afterward.
        </DialogDescription>
      </DialogHeader>

      <div className="flex gap-2 mt-2">
        <Input
          placeholder="Search canned jobs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
          autoFocus
        />
        {categories.length > 0 && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <ScrollArea className="h-[360px] rounded-md border mt-3">
        {isLoading ? (
          <div className="flex items-center justify-center text-sm text-muted-foreground gap-2 py-12">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading canned jobs…
          </div>
        ) : orderedJobs.length === 0 ? (
          <EmptyState
            hasSearch={isSearching || category !== '__all__'}
            query={deferredSearch}
            onClose={onClose}
          />
        ) : isSearching ? (
          <ul className="divide-y">
            {flat.map((job, idx) => renderRow(job, idx))}
          </ul>
        ) : (
          <>
            {favorites.length > 0 && (
              <Section icon={<Star className="h-3.5 w-3.5" />} title="Favorites">
                {favorites.map((job, i) => renderRow(job, i))}
              </Section>
            )}
            {recent.length > 0 && (
              <Section icon={<Clock className="h-3.5 w-3.5" />} title="Recent">
                {recent.map((job, i) => renderRow(job, favorites.length + i))}
              </Section>
            )}
            {all.length > 0 && (
              <Section title="All canned jobs">
                {all.map((job, i) =>
                  renderRow(job, favorites.length + recent.length + i),
                )}
              </Section>
            )}
          </>
        )}
      </ScrollArea>

      <DialogFooter className="mt-3">
        <p className="text-[11px] text-muted-foreground mr-auto self-center">
          Tip: ↑/↓ to navigate, Enter to apply
        </p>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="px-3 py-1.5 bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        {icon}
        {title}
      </div>
      <ul className="divide-y">{children}</ul>
    </div>
  );
}

interface RowProps {
  job: CannedJobListItem;
  shopId: string;
  focused: boolean;
  pending: boolean;
  anyPending: boolean;
  onApply: (job: CannedJobListItem) => void;
  onFocus: () => void;
}

const CannedJobRow = memo(function CannedJobRow({
  job,
  shopId,
  focused,
  pending,
  anyPending,
  onApply,
  onFocus,
}: RowProps) {
  const handleClick = useCallback(() => {
    if (anyPending) return;
    onApply(job);
  }, [anyPending, job, onApply]);

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={onFocus}
        disabled={anyPending}
        className={cn(
          'w-full text-left px-3 py-2.5 hover:bg-accent transition-colors relative',
          focused && 'bg-accent',
          anyPending && !pending && 'opacity-50',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{job.name}</span>
              {job.category && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  {job.category}
                </Badge>
              )}
            </div>
            {job.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                {job.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <FavoriteToggle
              shopId={shopId}
              cannedJobId={job.id}
              isFavorite={job.is_favorite}
              size={14}
            />
            <Badge variant="secondary" className="text-[10px]">
              {job.line_count} line{job.line_count === 1 ? '' : 's'}
            </Badge>
          </div>
        </div>
        {pending && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        )}
      </button>
    </li>
  );
});

function EmptyState({
  hasSearch,
  query,
  onClose,
}: {
  hasSearch: boolean;
  query: string;
  onClose: () => void;
}) {
  const { isManagerView } = useUserRole();
  return (
    <div className="flex flex-col items-center justify-center text-sm text-muted-foreground py-12 px-6 text-center">
      <PackageCheck className="h-6 w-6 mb-2 opacity-50" />
      {hasSearch ? (
        <p>
          No canned jobs match {query.trim() ? <>“{query.trim()}”</> : 'the current filter'}.
        </p>
      ) : (
        <>
          <p className="mb-2">No active canned jobs in this shop.</p>
          {isManagerView && (
            <Link
              to="/canned-jobs"
              onClick={onClose}
              className="text-primary hover:underline text-xs"
            >
              Manage templates →
            </Link>
          )}
        </>
      )}
    </div>
  );
}
